import clsx from "clsx";
import { default as React, useCallback, useEffect, useState } from "react";
import { api } from "../../../api";
import LoadingSpinner from "../../../externalComponents/LoadingSpinner";
import { Button } from "../../../externalComponents/tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../externalComponents/tailwind/dialog";
import { Text } from "../../../externalComponents/tailwind/text";
import { encodeOgg } from "./encode-ogg";
import { SendAudioDialogPermissionDenied } from "./SendAudioDialogPermissionDenied";

export function SendAudioDialog({
  modalRef,
  setModalRef,
  customerId,
  phoneNumber,
  whatsappName,
}) {
  const [recordData, setRecordData] = useState({
    recordState: "NONE",
    isRecordSent: false,
    fileUrl: null,
    sending: false,
    listening: false,
    permissionDenied: false,
  });

  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioStream, setAudioStream] = useState(null);

  useEffect(() => {
    if (!modalRef.sendAudio && recordData.recordState !== "NONE") {
      resetRecorder();
    }
  }, [recordData.recordState, modalRef.sendAudio]);

  const resetRecorder = useCallback(
    (options) => {
      if (mediaRecorder) {
        try {
          mediaRecorder.stop();
        } catch (e) {
          console.warn("MediaRecorder already stopped.", e);
        }
        setMediaRecorder(null);
      }

      if (audioStream) {
        audioStream.getTracks().forEach((track) => track.stop());
        setAudioStream(null);
      }

      setRecordData({
        recordState: options?.recordState || "NONE",
        isRecordSent: options?.isRecordSent || false,
        fileUrl: null,
        sending: false,
        listening: false,
        permissionDenied: false,
      });
    },
    [mediaRecorder, audioStream],
  );

  const closeModal = useCallback(() => {
    setModalRef((prev) => ({ ...prev, sendAudio: false }));
    resetRecorder();
  }, [setModalRef, resetRecorder]);

  const updateListening = useCallback((listening) => {
    setRecordData((prev) => ({ ...prev, listening }));
  }, []);

  const recordAudioMessage = useCallback(async () => {
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      resetRecorder({ recordState: "START" });
    } catch {
      setRecordData((prev) => ({
        ...prev,
        permissionDenied: true,
        recordState: "NONE",
      }));
      return;
    }

    setAudioStream(stream);

    try {
      const recorder = new MediaRecorder(stream);
      const localChunks = [];

      recorder.ondataavailable = (event) => localChunks.push(event.data);

      recorder.onstop = async () => {
        const blob = new Blob(localChunks, { type: "audio/ogg" });

        try {
          encodeOgg(blob, async (dataBlob, fileName) => {
            const response = await api.media.create({
              customer: { _link: customerId },
              file: { file: dataBlob, fileName },
              type: "audio",
            });
            if (modalRef.sendAudio) {
              setRecordData((prev) => ({
                ...prev,
                recordState: "STOP",
                isRecordSent: false,
                fileUrl: response?.file?.url || null,
                sending: false,
              }));
            }
          });
        } catch {
          resetRecorder();
        }
      };

      recorder.start();
      setMediaRecorder(recorder);
    } catch {
      resetRecorder();
    }
  }, [modalRef.sendAudio, customerId, resetRecorder]);

  const stopRecording = useCallback(() => {
    setRecordData((prev) => ({ ...prev, recordState: "STOP" }));

    if (mediaRecorder) {
      mediaRecorder.stop();
      setMediaRecorder(null);
    }

    if (audioStream) {
      audioStream.getTracks().forEach((track) => track.stop());
      setAudioStream(null);
    }
  }, [mediaRecorder, audioStream]);

  const listenAudioMessage = useCallback(async () => {
    if (!recordData.fileUrl) {
      return;
    }

    try {
      const response = await fetch(recordData.fileUrl);
      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);

      updateListening(true);
      audio.play();
      audio.onended = () => updateListening(false);
    } catch (error) {
      console.error("Error while playing audio:", error);
      updateListening(false);
    }
  }, [recordData.fileUrl, updateListening]);

  const sendAudioMessage = useCallback(async () => {
    setRecordData((prev) => ({ ...prev, sending: true }));

    try {
      await api.sendWhatsappFile({
        customerId,
        contactNumber: phoneNumber,
        fileUrl: recordData.fileUrl,
        fileType: "audio",
      });

      resetRecorder({ isRecordSent: true });
    } catch (error) {
      console.error("Error sending audio message:", error);
      setRecordData((prev) => ({ ...prev, sending: false }));
    }
  }, [customerId, phoneNumber, recordData.fileUrl]);

  return (
    <Dialog open={modalRef.sendAudio} onClose={closeModal}>
      <DialogTitle>Record and answer with an audio</DialogTitle>
      <DialogBody>
        <div
          className={clsx(
            "max-w-[300px] text-center mx-auto pt-[30px]",
            recordData.permissionDenied && "max-w-[500px]",
          )}
        >
          <Text className="text-md">
            Here you can record and send a voice note to{" "}
            <strong>{whatsappName}</strong>
          </Text>
          <br />

          {recordData.permissionDenied && <SendAudioDialogPermissionDenied />}

          {("START" === recordData.recordState ||
            ("STOP" === recordData.recordState && !recordData.fileUrl) ||
            recordData.sending) && (
            <div className="h-[50px]">
              <LoadingSpinner />
            </div>
          )}

          {recordData.fileUrl &&
            recordData.recordState === "STOP" &&
            !recordData.sending && (
              <div className="flex flex-col gap-3">
                <Button
                  className="w-full"
                  color="secondaryButton"
                  disabled={recordData.listening}
                  loading={recordData.listening}
                  onClick={listenAudioMessage}
                >
                  Listen my message
                </Button>
                <Button
                  className="w-full"
                  color="secondaryButton"
                  onClick={recordAudioMessage}
                >
                  Change my message
                </Button>
              </div>
            )}

          {recordData.recordState === "NONE" && recordData.isRecordSent && (
            <Text className="font-bold mt-5">
              Voice note sent successfully 🎉
            </Text>
          )}
        </div>
      </DialogBody>

      <DialogActions>
        <Button color="secondaryButton" onClick={closeModal}>
          Close
        </Button>

        {recordData.recordState === "NONE" && (
          <Button color="primaryButton" onClick={recordAudioMessage}>
            Record
          </Button>
        )}

        {recordData.recordState === "START" && (
          <Button color="primaryButton" onClick={stopRecording}>
            Stop
          </Button>
        )}

        {recordData.recordState === "STOP" && (
          <Button
            className="w-full"
            color="primaryButton"
            disabled={!recordData.fileUrl || recordData.sending}
            onClick={sendAudioMessage}
          >
            Send
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
